// WHAT: Modal component for creating new notifications
// WHY:  Provides a user-friendly interface for creating notifications with title and message
// DOMAIN: Admin → Notifications
// DEPENDENCIES: HeadlessUI Dialog, UI components (Button, Input)
// RISKS:
//   - NOTE: Form validation is minimal - add proper validation in production
//   - NOTE: onCreate callback must handle errors appropriately

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { type FC, useState } from "react";

import { Button, Input } from "@/components/ui";

/**
 * Props for CreateNotificationModal component
 */
interface Properties {
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback to create notification (async or sync) */
  onCreate: (title: string, message: string) => Promise<void> | void;
  /** Whether the modal is open */
  open: boolean;
}

/**
 * WHAT:
 *   Modal component for creating new notifications with title and message fields.
 *
 * WHY:
 *   Encapsulates notification creation logic in a reusable modal component
 *   that can be used across the application.
 *
 * PROPS:
 *   - open: boolean — Controls modal visibility
 *   - onClose: () => void — Handler to close the modal
 *   - onCreate: (title: string, message: string) => Promise<void> | void — Handler to create notification
 *
 * BEHAVIOR:
 *   - Manages local form state (title, message)
 *   - Validates that both fields are filled before submission
 *   - Clears form fields after successful creation
 *   - Handles async onCreate callback
 *
 * RISKS:
 *   - NOTE: Form validation only checks for empty strings - add length/format validation
 *   - NOTE: onCreate errors are not handled - add error handling in parent component
 *   - NOTE: Modal doesn't prevent closing during async operation
 *
 * @param root0
 *
 * @param root0.open
 *
 * @param root0.onClose
 *
 * @param root0.onCreate
 *
 * @returns {JSX.Element} Die gerenderte CreateNotificationModal-Komponente
 *
 */
export const CreateNotificationModal: FC<Properties> = ({ onClose, onCreate, open }) => {
  // NOTE: Form state for notification title
  const [title, setTitle] = useState("");
  // NOTE: Form state for notification message
  const [message, setMessage] = useState("");

  /**
   * WHAT:
   *   Handles notification creation with form validation.
   *
   * WHY:
   *   Centralizes creation logic and ensures form is cleared after submission.
   *
   * BEHAVIOR:
   *   - Validates that title and message are not empty
   *   - Calls onCreate callback with form data
   *   - Clears form fields after successful creation
   *
   * RISKS:
   *   - NOTE: No error handling if onCreate fails
   *   - NOTE: Validation is minimal - expand for production use
   */
  const handleCreate = async () => {
    if (!title || !message) {
      return;
    }
    await onCreate(title, message);
    setTitle("");
    setMessage("");
  };

  return (
    <Dialog className="relative z-50" onClose={onClose} open={open}>
      <div aria-hidden="true" className="modal-backdrop" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="modal-content space-y-4">
          <DialogTitle className="text-xl font-brand-medium">Neue Notification</DialogTitle>
          <Input
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder="Titel"
            value={title}
          />
          <textarea
            className="textarea-field"
            onChange={(e) => {
              setMessage(e.target.value);
            }}
            placeholder="Nachricht"
            rows={4}
            value={message}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onClose} variant="secondary">
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                void handleCreate();
              }}
              variant="primary"
            >
              Speichern
            </Button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
